import React, { useState, useEffect } from 'react';
import { TextField, Box, Typography, IconButton } from '@mui/material';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';
import { shades } from '../../../theme';

const ContactInfo = ({ onValidityChange, onDataChange, initialData = {} }) => {
  const [formData, setFormData] = useState({
    phones: [''],
    emails: [''],
    officeAddress: '',
    website: ''
  });

  const [errors, setErrors] = useState({});

  const textFieldStyle = {
    backgroundColor: '#ffffff',
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: shades.neutral[400]
      },
      '&:hover fieldset': {
        borderColor: shades.neutral[700]
      },
      '&.Mui-focused': {
        backgroundColor: '#ffffff !important',
        '& fieldset': {
          borderColor: 'black'
        }
      }
    },
    '& .MuiInputLabel-root': {
      color: shades.neutral[600],
      '&.Mui-focused': {
        color: 'black'
      }
    },
    '& .MuiOutlinedInput-input': {
      color: shades.neutral[800]
    },
    '& .MuiSelect-select': {
      color: shades.neutral[800]
    },
    '& input:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
      transition: 'background-color 5000s ease-in-out 0s'
    }
  };

  const iconButtonStyle = {
    color: shades.neutral[600],
    '&:hover': {
      color: 'black',
      backgroundColor: shades.neutral[100]
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Validate phone numbers
    if (formData.phones.some(phone => phone.trim() === '')) {
      newErrors.phones = 'At least one phone number is required.';
      isValid = false;
    } else if (formData.phones.some(phone => !/^\+?[0-9]{7,15}$/.test(phone))) {
      newErrors.phones = 'Enter a valid phone number.';
      isValid = false;
    }

    // Validate emails
    if (formData.emails.some(email => email.trim() === '')) {
      newErrors.emails = 'At least one email is required.';
      isValid = false;
    } else if (formData.emails.some(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) {
      newErrors.emails = 'Enter a valid email address.';
      isValid = false;
    }

    // Validate office address
    if (!formData.officeAddress.trim()) {
      newErrors.officeAddress = 'Office address is required.';
      isValid = false;
    }

    // Validate website if provided
    if (formData.website.trim() && !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(formData.website)) {
      newErrors.website = 'Enter a valid website URL.';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  useEffect(() => {
    const isValid = validateForm();
    onValidityChange(isValid);

    if (isValid) {
      onDataChange(formData);
    }
  }, [formData, onValidityChange, onDataChange]);

  const handleAddPhone = () => {
    setFormData({
      ...formData,
      phones: [...formData.phones, '']
    });
  };

  const handleRemovePhone = (indexToRemove) => {
    if (formData.phones.length > 1) {
      setFormData({
        ...formData,
        phones: formData.phones.filter((_, index) => index !== indexToRemove)
      });
    }
  };

  const handleAddEmail = () => {
    setFormData({
      ...formData,
      emails: [...formData.emails, '']
    });
  };

  const handleRemoveEmail = (indexToRemove) => {
    if (formData.emails.length > 1) {
      setFormData({
        ...formData,
        emails: formData.emails.filter((_, index) => index !== indexToRemove)
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Phone Numbers */}
      <Box>
        <Typography variant='subtitle1' sx={{ color: shades.neutral[800], mb: 1 }}>
          Phone Number(s)
        </Typography>
        {formData.phones.map((phone, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TextField
              sx={textFieldStyle}
              label={`Phone ${index + 1}`}
              fullWidth
              value={phone}
              onChange={(e) => {
                const updatedPhones = [...formData.phones];
                updatedPhones[index] = e.target.value;
                setFormData({
                  ...formData,
                  phones: updatedPhones
                });
              }}
              error={!!errors.phones}
              helperText={index === 0 ? errors.phones : ''}
            />
            <IconButton
              onClick={() => handleRemovePhone(index)}
              sx={iconButtonStyle}
              disabled={formData.phones.length === 1}
            >
              <RemoveCircleOutline />
            </IconButton>
          </Box>
        ))}
        <IconButton
          onClick={handleAddPhone}
          sx={iconButtonStyle}
        >
          <AddCircleOutline />
        </IconButton>
      </Box>

      {/* Email Addresses */}
      <Box>
        <Typography variant='subtitle1' sx={{ color: shades.neutral[800], mb: 1 }}>
          Email Address(es)
        </Typography>
        {formData.emails.map((email, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TextField
              sx={textFieldStyle}
              label={`Email ${index + 1}`}
              fullWidth
              value={email}
              onChange={(e) => {
                const updatedEmails = [...formData.emails];
                updatedEmails[index] = e.target.value;
                setFormData({
                  ...formData,
                  emails: updatedEmails
                });
              }}
              error={!!errors.emails}
              helperText={index === 0 ? errors.emails : ''}
            />
            <IconButton
              onClick={() => handleRemoveEmail(index)}
              sx={iconButtonStyle}
              disabled={formData.emails.length === 1}
            >
              <RemoveCircleOutline />
            </IconButton>
          </Box>
        ))}
        <IconButton
          onClick={handleAddEmail}
          sx={iconButtonStyle}
        >
          <AddCircleOutline />
        </IconButton>
      </Box>

      {/* Office Address */}
      <TextField
        sx={textFieldStyle}
        label='Office Address'
        fullWidth
        required
        value={formData.officeAddress}
        onChange={(e) => setFormData({
          ...formData,
          officeAddress: e.target.value
        })}
        error={!!errors.officeAddress}
        helperText={errors.officeAddress}
      />

      {/* Website (Optional) */}
      <TextField
        sx={textFieldStyle}
        label='Website (Optional)'
        fullWidth
        value={formData.website}
        onChange={(e) => setFormData({
          ...formData,
          website: e.target.value
        })}
        error={!!errors.website}
        helperText={errors.website}
      />
    </Box>
  );
};

export default ContactInfo;
